import type { StandardSchemaV1 } from "@standard-schema/spec";
import type { Schema } from "drizzle-orm";
import type { GencowCtx, GencowDefaultUserIdentity, GencowEmptyPgSchema } from "./context.js";

export type GencowProcedureKind = "query" | "mutation" | "internal";

type InferInputSchema<TSchema> = TSchema extends { "~standard": { types: { input: infer TInput } } }
  ? TInput
  : TSchema extends StandardSchemaV1
    ? StandardSchemaV1.InferInput<TSchema>
    : unknown;

type InferOutputSchema<TSchema> = TSchema extends { "~standard": { types: { output: infer TOutput } } }
  ? TOutput
  : TSchema extends { _output: infer TOutput }
    ? TOutput
    : TSchema extends StandardSchemaV1
      ? StandardSchemaV1.InferOutput<TSchema>
      : unknown;

type NextFn<TContext, TInput, TOutput> = (overrides?: {
  context?: TContext;
  input?: TInput;
}) => Promise<TOutput>;

export type GencowProcedureMiddleware<TContext, TInput, TOutput> = (options: {
  context: TContext;
  input: TInput;
  next: NextFn<TContext, TInput, TOutput>;
}) => Promise<TOutput> | TOutput;

export type GencowProcedureHandler<TContext, TInput, TOutput> = (options: {
  context: TContext;
  input: TInput;
}) => Promise<TOutput> | TOutput;

export interface GencowProcedureDef<
  TKind extends GencowProcedureKind = GencowProcedureKind,
  TContext = any,
  TInput = unknown,
  TOutput = unknown,
> {
  kind: TKind;
  name: string;
  /** When false (default), RPC requires an authenticated user. */
  allowAnonymous: boolean;
  middlewares: readonly GencowProcedureMiddleware<TContext, TInput, TOutput>[];
  inputSchema?: StandardSchemaV1;
  outputSchema?: StandardSchemaV1;
  inputValidationIndex: number;
  outputValidationIndex: number;
  handler: (context: TContext, input: TInput) => Promise<TOutput>;
}

export interface GencowProcedureBuilderBase<
  TKind extends GencowProcedureKind = GencowProcedureKind,
  TContext = any,
  TInput = unknown,
  TOutput = unknown,
> {
  name(name: string): GencowProcedureBuilderBase<TKind, TContext, TInput, TOutput>;
  use(
    middleware: GencowProcedureMiddleware<TContext, TInput, TOutput>,
  ): GencowProcedureBuilderBase<TKind, TContext, TInput, TOutput>;
  input<TNextSchema extends StandardSchemaV1>(
    schema: TNextSchema,
  ): GencowProcedureBuilderBase<TKind, TContext, InferInputSchema<TNextSchema>, TOutput>;
  output<TNextSchema extends StandardSchemaV1>(
    schema: TNextSchema,
  ): GencowProcedureBuilderBase<TKind, TContext, TInput, InferOutputSchema<TNextSchema>>;
  handler(
    handler: GencowProcedureHandler<TContext, TInput, TOutput>,
  ): GencowProcedureDef<TKind, TContext, TInput, TOutput>;
}

export interface GencowProcedureBuilder<
  TKind extends "query" | "mutation" = "query" | "mutation",
  TContext = any,
  TInput = unknown,
  TOutput = unknown,
> extends GencowProcedureBuilderBase<TKind, TContext, TInput, TOutput> {
  name(name: string): GencowProcedureBuilder<TKind, TContext, TInput, TOutput>;
  /**
   * Allow calling this procedure without authentication (like legacy `query("x", { public: true, ... })`).
   * Default is authenticated-only.
   */
  allowAnonymous(): GencowProcedureBuilder<TKind, TContext, TInput, TOutput>;
  use(
    middleware: GencowProcedureMiddleware<TContext, TInput, TOutput>,
  ): GencowProcedureBuilder<TKind, TContext, TInput, TOutput>;
  input<TNextSchema extends StandardSchemaV1>(
    schema: TNextSchema,
  ): GencowProcedureBuilder<TKind, TContext, InferInputSchema<TNextSchema>, TOutput>;
  output<TNextSchema extends StandardSchemaV1>(
    schema: TNextSchema,
  ): GencowProcedureBuilder<TKind, TContext, TInput, InferOutputSchema<TNextSchema>>;
}

export interface GencowInternalProcedureBuilder<
  TContext = any,
  TInput = unknown,
  TOutput = unknown,
> extends GencowProcedureBuilderBase<"internal", TContext, TInput, TOutput> {
  name(name: string): GencowInternalProcedureBuilder<TContext, TInput, TOutput>;
  use(
    middleware: GencowProcedureMiddleware<TContext, TInput, TOutput>,
  ): GencowInternalProcedureBuilder<TContext, TInput, TOutput>;
  input<TNextSchema extends StandardSchemaV1>(
    schema: TNextSchema,
  ): GencowInternalProcedureBuilder<TContext, InferInputSchema<TNextSchema>, TOutput>;
  output<TNextSchema extends StandardSchemaV1>(
    schema: TNextSchema,
  ): GencowInternalProcedureBuilder<TContext, TInput, InferOutputSchema<TNextSchema>>;
}

function hasStandardSchema(schema: unknown): schema is StandardSchemaV1 {
  return (
    !!schema &&
    typeof schema === "object" &&
    "~standard" in schema &&
    !!(schema as any)["~standard"] &&
    typeof (schema as any)["~standard"].validate === "function"
  );
}

async function validateWithSchema<TOutput>(schema: unknown, value: unknown): Promise<TOutput> {
  if (!schema) return value as TOutput;

  if (hasStandardSchema(schema)) {
    const result = (await schema["~standard"].validate(value)) as
      | { value: TOutput }
      | { issues: readonly { message: string }[] };
    if ("issues" in result && result.issues.length > 0) {
      throw new Error(result.issues[0]?.message ?? "Validation failed");
    }
    if ("value" in result) {
      return result.value as TOutput;
    }
    throw new Error("Validation failed");
  }

  throw new Error("procedure.query / procedure.mutation / procedure.internal only supports Standard Schema v1 validators");
}

function composeMiddlewares<TContext, TInput, TOutput>(
  middlewares: Array<GencowProcedureMiddleware<TContext, TInput, TOutput>>,
  handler: GencowProcedureHandler<TContext, TInput, TOutput>,
  inputSchema: StandardSchemaV1 | undefined,
  outputSchema: StandardSchemaV1 | undefined,
  inputValidationIndex: number,
  outputValidationIndex: number,
): (context: TContext, input: TInput) => Promise<TOutput> {
  return async (initialContext, initialInput) => {
    let idx = -1;
    const boundedInputValidationIndex = Math.min(Math.max(0, inputValidationIndex), middlewares.length);
    const boundedOutputValidationIndex = Math.min(Math.max(0, outputValidationIndex), middlewares.length);

    const run = async (currentIdx: number, context: TContext, input: TInput): Promise<TOutput> => {
      if (currentIdx <= idx) {
        throw new Error("next() called multiple times in the same middleware");
      }
      idx = currentIdx;
      let currentInput = input;

      if (currentIdx === boundedInputValidationIndex) {
        currentInput = (await validateWithSchema(inputSchema, currentInput)) as TInput;
      }

      if (currentIdx === middlewares.length) {
        const output = await handler({ context, input: currentInput });
        if (currentIdx === boundedOutputValidationIndex) {
          return await validateWithSchema<TOutput>(outputSchema, output);
        }
        return output;
      }

      const middleware = middlewares[currentIdx];
      const output = await middleware({
        context,
        input: currentInput,
        next: async (overrides) => {
          return await run(
            currentIdx + 1,
            (overrides?.context ?? context) as TContext,
            (overrides?.input ?? currentInput) as TInput,
          );
        },
      });

      if (currentIdx === boundedOutputValidationIndex) {
        return await validateWithSchema<TOutput>(outputSchema, output);
      }

      return output;
    };

    return await run(0, initialContext, initialInput);
  };
}

class GencowProcedureBuilderImpl<
  TKind extends GencowProcedureKind,
  TContext = any,
  TInput = unknown,
  TOutput = unknown,
> implements GencowProcedureBuilderBase<TKind, TContext, TInput, TOutput> {
  constructor(
    private readonly kind: TKind,
    private readonly procedureName?: string,
    private readonly middlewares: Array<GencowProcedureMiddleware<TContext, TInput, TOutput>> = [],
    private readonly inputSchema?: StandardSchemaV1,
    private readonly outputSchema?: StandardSchemaV1,
    private readonly inputValidationIndex = -1,
    private readonly outputValidationIndex = -1,
    private readonly anonymousAllowed = false,
  ) {}

  name(name: string) {
    return new GencowProcedureBuilderImpl<TKind, TContext, TInput, TOutput>(
      this.kind,
      name,
      this.middlewares,
      this.inputSchema,
      this.outputSchema,
      this.inputValidationIndex,
      this.outputValidationIndex,
      this.anonymousAllowed,
    );
  }

  allowAnonymous() {
    return new GencowProcedureBuilderImpl<TKind, TContext, TInput, TOutput>(
      this.kind,
      this.procedureName,
      this.middlewares,
      this.inputSchema,
      this.outputSchema,
      this.inputValidationIndex,
      this.outputValidationIndex,
      true,
    );
  }

  use(middleware: GencowProcedureMiddleware<TContext, TInput, TOutput>) {
    return new GencowProcedureBuilderImpl<TKind, TContext, TInput, TOutput>(
      this.kind,
      this.procedureName,
      [...this.middlewares, middleware],
      this.inputSchema,
      this.outputSchema,
      this.inputValidationIndex,
      this.outputValidationIndex,
      this.anonymousAllowed,
    );
  }

  input<TNextSchema extends StandardSchemaV1>(schema: TNextSchema) {
    const nextInputValidationIndex = this.middlewares.length < 0 ? 0 : this.middlewares.length;

    return new GencowProcedureBuilderImpl<TKind, TContext, InferInputSchema<TNextSchema>, TOutput>(
      this.kind,
      this.procedureName,
      this.middlewares as Array<GencowProcedureMiddleware<TContext, InferInputSchema<TNextSchema>, TOutput>>,
      schema,
      this.outputSchema,
      nextInputValidationIndex,
      this.outputValidationIndex,
      this.anonymousAllowed,
    );
  }

  output<TNextSchema extends StandardSchemaV1>(schema: TNextSchema) {
    const nextOutputValidationIndex = this.middlewares.length < 0 ? 0 : this.middlewares.length;

    return new GencowProcedureBuilderImpl<TKind, TContext, TInput, InferOutputSchema<TNextSchema>>(
      this.kind,
      this.procedureName,
      this.middlewares as Array<GencowProcedureMiddleware<TContext, TInput, InferOutputSchema<TNextSchema>>>,
      this.inputSchema,
      schema,
      this.inputValidationIndex,
      nextOutputValidationIndex,
      this.anonymousAllowed,
    );
  }

  handler(
    handler: GencowProcedureHandler<TContext, TInput, TOutput>,
  ): GencowProcedureDef<TKind, TContext, TInput, TOutput> {
    if (!this.procedureName) {
      throw new Error("Procedure name is required. Call .name(...) before .handler(...)");
    }

    const run = composeMiddlewares(
      this.middlewares,
      handler,
      this.inputSchema,
      this.outputSchema,
      this.inputValidationIndex,
      this.outputValidationIndex,
    );

    return {
      kind: this.kind,
      name: this.procedureName,
      allowAnonymous: this.kind === "internal" ? false : this.anonymousAllowed,
      middlewares: [...this.middlewares],
      inputSchema: this.inputSchema,
      outputSchema: this.outputSchema,
      inputValidationIndex: this.inputValidationIndex,
      outputValidationIndex: this.outputValidationIndex,
      handler: run,
    };
  }
}

export type GencowProcedureNamespace<
  TSchema extends Schema = GencowEmptyPgSchema,
  TUser extends GencowDefaultUserIdentity = GencowDefaultUserIdentity,
> = {
  readonly query: GencowProcedureBuilder<"query", GencowCtx<TSchema, TUser>>;
  readonly mutation: GencowProcedureBuilder<"mutation", GencowCtx<TSchema, TUser>>;
  readonly internal: GencowInternalProcedureBuilder<GencowCtx<TSchema, TUser>>;
};

/**
 * Canonical factory for typed `procedure.query` / `procedure.mutation` / `procedure.internal`. Bind once in app
 * `gencow/runtime.ts` with `typeof schema` and optional `AppUser` (see spec-typed-app-context).
 */
export function createGencowProcedureBuilders<
  TSchema extends Schema = GencowEmptyPgSchema,
  TUser extends GencowDefaultUserIdentity = GencowDefaultUserIdentity,
>(): GencowProcedureNamespace<TSchema, TUser> {
  return {
    query: new GencowProcedureBuilderImpl<"query", GencowCtx<TSchema, TUser>>("query"),
    mutation: new GencowProcedureBuilderImpl<"mutation", GencowCtx<TSchema, TUser>>("mutation"),
    internal: new GencowProcedureBuilderImpl<"internal", GencowCtx<TSchema, TUser>>("internal"),
  };
}
